import { defineStore, storeToRefs } from "pinia"
import { computed, inject, ref } from "vue"
import { useFetchWrapper } from "@core/composables"
import { Services, IrveAnalysis, BuildingSource } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"

export const useIrveStore = defineStore("irve", () => {
    const {
        $getAnalysesByBuilding,
        $deleteAnalysisById,
        $editAnalysisName,
        $getAnalysisById,
    } = inject<Services>("services")!

    const buildingStore = useBuildingStore()

    const { building } = storeToRefs(buildingStore)

    // =============
    // States
    // =============

    const analyses = ref<IrveAnalysis[] | null>(null)

    const currentAnalysisId = ref<string | null>(null)

    // =============
    // Actions
    // =============

    const fetchAnalyses = useFetchWrapper(async () => {
        if (building.value) {
            const { data } = await $getAnalysesByBuilding(building.value?._id)
            analyses.value = data
        }
    })

    const fetchCurrentAnalysis = useFetchWrapper(
        async (
            { _id_analysis }: { _id_analysis?: string } = {
                _id_analysis: undefined,
            },
            controller = new AbortController()
        ) => {
            if (!analyses.value) return

            const analysisId = _id_analysis
                ? _id_analysis
                : analyses.value[analyses?.value.length - 1]?._id_analysis

            const analyse = analyses.value.find(
                (analyse) => analyse._id_analysis === analysisId
            )

            if (!analyse) return

            // met à jour l'analyse courante
            currentAnalysisId.value = analysisId

            // si les graphiques ne sont pas déjà chargés
            if (!analyse.outputs?.graphs) {
                const result = await $getAnalysisById(analysisId, controller)

                if (result) {
                    const analyseIndex = analyses.value.findIndex(
                        (analyse) => analyse._id_analysis === analysisId
                    )
                    analyses.value[analyseIndex] = result
                }
            }
        }
    )

    /**
     * Fonction qui permet de renvoyer une fonction de fetch pour télécharger le rapport d'une analyse
     * avec les états de l'appel API
     */
    function getDownloadReportFetch() {
        return useFetchWrapper(async (analysisId) => {
            try {
                if (analyses.value) {
                    const analysis = analyses.value.find(
                        (analysis) => analysis._id_analysis === analysisId
                    )
                    if (analysis) {
                        // TODO Ajouter le téléchargement du fichier
                    }
                }
            } catch (error) {
                console.error("Error fetching analyses:", error)
            }
        })
    }

    function getEditAnalysisNameFetch() {
        return useFetchWrapper(
            async ({
                _id_analysis,
                name,
            }: {
                _id_analysis: string
                name: string
            }) => {
                if (!analyses.value) return

                const analysis = analyses.value.find(
                    (analysis) => analysis._id_analysis === _id_analysis
                )

                if (!analysis) return

                const previousName = analysis.name

                try {
                    analysis.name = name
                    await $editAnalysisName(_id_analysis, name)
                } catch {
                    analysis.name = previousName
                }
            }
        )
    }

    /**
     * Fonction permettant de gérer la suppresion d'une analyse avec les états de l'appel API.
     * A utiliser dans les composants pour supprimer une analyse
     * @returns
     */
    function getDeleteAnalysisFetch() {
        return useFetchWrapper(
            async ({ _id_analysis }: { _id_analysis: string }) => {
                if (!analyses.value) return

                const analyseIndex = analyses.value.findIndex(
                    (analyse) => analyse._id_analysis === _id_analysis
                )
                const analyse = analyses.value[analyseIndex]

                if (analyseIndex === -1) return

                analyses.value = analyses.value.filter(
                    (analysis) => analysis._id_analysis !== _id_analysis
                )

                if (analyses.value.length > 0) {
                    fetchCurrentAnalysis.fetch({
                        _id_analysis:
                            analyses.value[analyses.value.length - 1]
                                ._id_analysis,
                    })
                }

                try {
                    await $deleteAnalysisById(_id_analysis)
                } catch (err) {
                    // Remet l'analyse supprimée dans la liste
                    analyses.value.splice(analyseIndex, 0, analyse)
                    throw err
                }
            }
        )
    }

    /**
     * Quand on clique sur une analyse à voir, on met à jour l'analyse courante
     * @param analysisId - id de l'analyse à voir
     */
    async function viewAnalysis(analysisId: string) {
        // si l'analyse est entrain de se charger, on annule la requête
        if (fetchCurrentAnalysis.isFetching.value) {
            fetchCurrentAnalysis.cancel()
        }

        fetchCurrentAnalysis.fetch({ _id_analysis: analysisId })
    }

    // =============
    // Getters
    // =============

    /**
     * Récupérer une analyse par son id
     * @param analyseId - id de l'analyse
     * @returns l'analyse correspondante
     */
    const getAnalysisById = (analyseId: string) => {
        return analyses.value?.find(
            (analyse) => analyse._id_analysis === analyseId
        )
    }

    /**
     * Récupérer l'analyse courante affichée dans le composant IrveView
     * @returns l'analyse courante
     */
    const getCurrentAnalysis = computed(() => {
        return getAnalysisById(currentAnalysisId.value!)
    })

    /**
     * Récupérer le compteur de l'analyse courante
     */
    const getCurrentAnalyseMeter = computed(() => {
        if (currentAnalysisId.value && analyses.value) {
            // récupère l'id de la source
            const _id_source = getCurrentAnalysis.value?.inputs._id_source

            // récupère la source dans le bâtiment
            const source = building.value?.sources.find(
                (source: BuildingSource) => source._id_source === _id_source
            )

            return source
        }
        return null
    })

    const isFetchingAnalysis = computed(
        () =>
            fetchCurrentAnalysis.isFetching.value ||
            fetchAnalyses.isFetching.value
    )

    /**
     * Liste des compteurs électriques utilisables pour l'analyse de puissance résiduelle
     */
    const getListPdlForResidualPowerAnalysis = computed(() => {
        return (
            building.value?.sources?.filter((s: BuildingSource) =>
                ["C4", "C2"].includes(
                    s?.operation?.pdl?.enedis?.donnees_generales?.segment
                        ?.code ?? ""
                )
            ) ?? []
        )
    })

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset() {
        analyses.value = null
        currentAnalysisId.value = null
    }

    return {
        fetchAnalyses,
        fetchCurrentAnalysis,
        isFetchingAnalysis,
        viewAnalysis,
        getDownloadReportFetch,
        getEditAnalysisNameFetch,
        getDeleteAnalysisFetch,
        getAnalysisById,
        currentAnalysisId,
        getCurrentAnalysis,
        getCurrentAnalyseMeter,
        analyses: analyses,
        building,
        getListPdlForResidualPowerAnalysis,
        $reset,
    }
})
